import { useState, useEffect, useRef } from "react";
import CalendarPicker from "./Calendarpicker";
import ProductSelector from "./Productselector";
import { X } from "lucide-react";
import emailjs from "@emailjs/browser";

 const sendDemoBookingEmail = async (userData) => {
  try {
    const templateParams = {
      user_name: userData.name,
      user_email: userData.email,
      products: userData.products.join(", "),
      demo_date: userData.date?.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      demo_time: userData.time,
      submitted_at: new Date().toLocaleString(),
    };

    const response = await emailjs.send(
      "service_38jah5q",
      "template_txyhs0b",
      templateParams,
      "ombndUcBIWaV_7W88"
    );

    console.log("Email sent successfully:", response);

    return {
      success: true,
      response,
    };
  } catch (error) {
    console.error("EmailJS Error:", error);

    return {
      success: false,
      error,
    };
  }
};


export default function DemoBookingAssistant({ isOpen, onClose, autoStart = false }) {
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState("greeting");
  const [inputVal, setInputVal] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [demoStarted, setDemoStarted] = useState(false);  // ← NEW: true once booking begins
  const [userData, setUserData] = useState({
    email: "", name: "", products: [], date: null, time: null,
  });

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasGreeted = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && !hasGreeted.current) {
      hasGreeted.current = true;
      if (autoStart) {
        setTimeout(() => {
          botSay("Hi there! 👋 Let's get your demo booked!");
          setTimeout(() => {
            setDemoStarted(true);
            userSay("I whould like to Book a demo");
            withTyping(() => {
              setStep("email");
              botSay("Sure 😊 What's your work email?");
            });
          }, 600);
        }, 400);
      } else {
        setTimeout(() => {
          botSay("Hi there! 👋 What can I help you explore about KGS Technology Group today?");
        }, 400);
      }
    }
  }, [isOpen, autoStart]);

  useEffect(() => {
    if (!isOpen) {
      hasGreeted.current = false;
      setMessages([]);
      setStep("greeting");
      setDemoStarted(false); // ← reset on close
    }
  }, [isOpen]);

  function addMessage(msg) { setMessages((prev) => [...prev, msg]); }
  function botSay(text, extra = {}) { addMessage({ id: Date.now() + Math.random(), role: "bot", text, ...extra }); }
  function userSay(text) { addMessage({ id: Date.now(), role: "user", text }); }
  function withTyping(cb, delay = 900) {
    setIsTyping(true);
    setTimeout(() => { setIsTyping(false); cb(); }, delay);
  }

  function handleSend() {
    const val = inputVal.trim();
    if (!val || inputDisabled) return;
    setInputVal("");
    userSay(val);
    processInput(val);
  }

  function processInput(val) {
    const v = val.toLowerCase();

    if (step === "greeting") {
      if (v.includes("demo") || v.includes("book")) {
        setDemoStarted(true); // ← Mark started when user TYPES it too
        setStep("email");
        withTyping(() => botSay("Sure 😊 What's your work email?"));
      } else {
        withTyping(() =>
          botSay('I\'d love to help! Ask me about KGS Technology\'s products or type "book a demo" to get started.')
        );
      }
      return;
    }

    if (step === "email") {
      if (val.includes("@") && val.includes(".")) {
        setUserData((prev) => ({ ...prev, email: val }));
        setStep("name");
        withTyping(() => botSay("Thanks! What's your full name?"));
      } else {
        withTyping(() => botSay("Please enter a valid work email address 📧"));
      }
      return;
    }

    if (step === "name") {
      setUserData((prev) => ({ ...prev, name: val }));
      setStep("products");
      setInputDisabled(true);
      withTyping(() => {
        botSay(`Great to meet you, ${val}! What products are you interested in? 🎯`, { type: "products" });
      });
      return;
    }

    if (step === "done") {
      withTyping(() =>
        botSay("I'm here to help! Feel free to ask me anything about KGS Technology group's supply chain solutions. 😊")
      );
      return;
    }
  }

  function handleProductsSubmit(selected) {
    setUserData((prev) => ({ ...prev, products: selected }));
    userSay(selected.join(", "));
    setStep("calendar");
    setInputDisabled(true);
    withTyping(() => { botSay("Pick a date for the demo 📅", { type: "calendar" }); });
  }

 async function handleCalendarSubmit(date, time) {
  const updatedData = {
    ...userData,
    date,
    time,
  };

  setUserData(updatedData);

  const label = `${date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })} at ${time}`;

  userSay(label);

  // SEND EMAIL
  await sendDemoBookingEmail(updatedData);

  setStep("done");
  setInputDisabled(false);

  withTyping(() => {
    botSay("", { type: "success", date, time });

    setTimeout(() => {
      botSay("Is there anything else I can help you with? 😊");
    }, 500);
  }, 1000);
}

  // Button visibility: hide ONLY during products/calendar inline steps
  const showBookButton = step !== "products" && step !== "calendar";

  function handleBookButton() {
    if (demoStarted) {
      // "Continue Chat" — friendly nudge
      botSay("You're back! How else can I help you today? 😊");
      return;
    }
    // "Book a Demo" — start the flow
    setDemoStarted(true);
    userSay("Book a demo");
    setStep("email");
    withTyping(() => botSay("Sure 😊 What's your work email?"));
  }

  return (
    <>
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[320px] sm:w-[380px] h-[520px] flex flex-col bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-600 to-yellow-700 px-5 py-5 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-semibold text-sm">AI</div>
            <div className="flex-1">
              <p className="text-white font-semibold text-xs sm:text-sm leading-tight">AI Assist | KGS Technology Group</p>
              <p className="text-blue-100 text-xs">Your Supply Chain Expert</p>
            </div>
            <div onClick={onClose} className="text-white cursor-pointer px-2"><X /></div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 scroll-smooth no-scrollbar">
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                userData={userData}
                onProductsSubmit={handleProductsSubmit}
                onCalendarSubmit={handleCalendarSubmit}
              />
            ))}
            {isTyping && (
              <div className="flex items-start gap-2">
                <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1 items-center">
                  <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce [animation-delay:0ms]" />
                  <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce [animation-delay:150ms]" />
                  <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Book / Continue button */}
          {showBookButton && (
            <div className="px-5 py-1.5">
              <button
                onClick={handleBookButton}
                className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm transition-all shadow-sm
                  ${demoStarted
                    ? "border border-green-400 text-green-500"
                    : "border border-orange-400 text-orange-400"
                  }`}
              >
                {demoStarted ? "💬 Continue Chat" : "📅 Book a Demo"}
              </button>
            </div>
          )}

          {/* Input bar */}
          <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2 bg-white flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              disabled={inputDisabled}
              placeholder={inputDisabled ? "Use the form above ☝️" : "Type a message..."}
              className="flex-1 border border-slate-200 rounded-full px-4 py-2 text-sm text-slate-800 outline-none focus:border-blue-500 transition-colors disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleSend}
              disabled={inputDisabled || !inputVal.trim()}
              className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 flex items-center justify-center transition-colors flex-shrink-0"
            >
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}



// ── Message Bubble ────────────────────────────────────────────────────────────
function MessageBubble({ msg, userData, onProductsSubmit, onCalendarSubmit }) {
  const isUser = msg.role === "user";

    if (msg.type === "products") {
    return (
        <div className="flex flex-col gap-2">
        <BotBubble text={msg.text} />

        {/* FIX HERE */}
        <div className="relative z-10 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm w-full max-h-[220px] overflow-y-auto no-scrollbar">
            <ProductSelector
            products={["EDIFAI","IQUA","WMS","Demand Forecasting","FairMiles","Others"]}
            onSubmit={onProductsSubmit}
            />
        </div>
        </div>
    );
}

  if (msg.type === "calendar") {
    return (
      <div className="flex flex-col gap-2">
        <BotBubble text={msg.text} />
        <CalendarPicker onSubmit={onCalendarSubmit} />
      </div>
    );
  }

  if (msg.type === "success") {
    const dateLabel = msg.date?.toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
    return (
      <div className="flex flex-col gap-2">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 text-center shadow-sm">
          <div className="text-4xl mb-3">🎉</div>
          <h4 className="font-semibold text-slate-800 text-base mb-1">Demo Booked!</h4>
          <p className="text-slate-500 text-sm">
            Your 30-min demo is confirmed for<br />
            <strong className="text-slate-700">{dateLabel}</strong><br />
            at <strong className="text-slate-700">{msg.time}</strong>
          </p>
          <p className="text-slate-500 text-sm mt-2">
            Confirmation will be sent to<br />
            <strong className="text-blue-600">{userData.email}</strong>
          </p>
        </div>
      </div>
    );
  }

  return isUser ? (
    <div className="flex justify-end">
      <div className="bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[85%] leading-relaxed animate-pop">
        {msg.text}
      </div>
    </div>
  ) : (
    <BotBubble text={msg.text} />
  );
}

function BotBubble({ text }) {
  return (
    <div className="flex justify-start">
      <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm max-w-[85%] leading-relaxed animate-pop">
        {text}
      </div>
    </div>
  );
}